import { and, eq } from "drizzle-orm/sql";

import {
  buildIndexingMetadataContextUninitialized,
  deserializeIndexingMetadataContext,
  type EnsDbPublicConfig,
  type EnsDbVersionInfo,
  type IndexingMetadataContext,
  IndexingMetadataContextStatusCodes,
} from "@ensnode/ensnode-sdk";

export {
  type IndexingMetadataContext,
  type IndexingMetadataContextInitialized,
  IndexingMetadataContextStatusCodes,
  type IndexingMetadataContextUninitialized,
} from "@ensnode/ensnode-sdk";

import {
  type AbstractEnsIndexerSchema,
  buildEnsDbDrizzleClient,
  buildIndividualEnsDbSchemas,
  type EnsDbDrizzleClient,
  type EnsNodeSchema,
} from "../lib/drizzle";
import { parsePgVersionInfo } from "../lib/parse-pg-version-info";
import { EnsNodeMetadataKeys } from "./ensnode-metadata";
import type {
  SerializedEnsNodeMetadata,
  SerializedEnsNodeMetadataIndexingMetadataContext,
} from "./serialize/ensnode-metadata";

/**
 * Re-export the ENSDb Drizzle Client type for external use in building
 * custom ENSDb queries with proper typing of the "concrete" ENSIndexer Schema
 * from the ENSDbReader instance.
 */
export type { EnsDbDrizzleClient } from "../lib/drizzle";

/**
 * ENSDb Reader
 *
 * Enables read-only querying of an ENSDb instance, including data spanning
 * the ENSNode Schema and the specified ENSIndexer Schema.
 *
 * Note: we use a parameter type `ConcreteEnsIndexerSchema` to represent
 * the "concrete" ENSIndexer Schema type within the ENSDb Schema and
 * make sure that the Drizzle client used for querying is typed with
 * the same "concrete" ENSIndexer Schema type.
 */
export class EnsDbReader<
  ConcreteEnsIndexerSchema extends AbstractEnsIndexerSchema = AbstractEnsIndexerSchema,
> {
  /**
   * Drizzle client for ENSDb.
   *
   * Uses the ENSDb Schema from {@link ensDbSchema}.
   */
  protected drizzleClient: EnsDbDrizzleClient<ConcreteEnsIndexerSchema>;

  /**
   * "Concrete" ENSIndexer Schema definition for ENSDb.
   *
   * This is the "concrete" ENSIndexer Schema in which tables reference
   * the ENSIndexer Schema name from {@link ensIndexerSchemaName}.
   */
  protected _concreteEnsIndexerSchema: ConcreteEnsIndexerSchema;

  /**
   * The name of the ENSIndexer schema to read from in ENSDb.
   *
   * This also identifies which ENSNode metadata records to read from the ENSNode Schema
   * as the ENSNode Schema is multi-tenant across ENSIndexer instances / ENSIndexer Schemas in an ENSDb.
   */
  protected _ensIndexerSchemaName: string;

  protected _ensNodeSchema: EnsNodeSchema;

  /**
   * @param ensDbUrl The connection string for Drizzle to connect to the ENSDb instance.
   * @param ensIndexerSchemaName The name of the ENSIndexer schema to read from in ENSDb, used to identify which ENSNode metadata records to read.
   */
  constructor(ensDbUrl: string, ensIndexerSchemaName: string) {
    const { concreteEnsIndexerSchema, ensNodeSchema } =
      buildIndividualEnsDbSchemas<ConcreteEnsIndexerSchema>(ensIndexerSchemaName);
    const ensDbDrizzleClient = buildEnsDbDrizzleClient<ConcreteEnsIndexerSchema>(
      ensDbUrl,
      concreteEnsIndexerSchema,
    );
    this.drizzleClient = ensDbDrizzleClient;
    this._concreteEnsIndexerSchema = concreteEnsIndexerSchema;
    this._ensIndexerSchemaName = ensIndexerSchemaName;
    this._ensNodeSchema = ensNodeSchema;
  }

  /**
   * Getter for the Drizzle client for ENSDb instance
   *
   * Useful while working on complex queries for ENSDb.
   */
  get ensDb(): EnsDbDrizzleClient<ConcreteEnsIndexerSchema> {
    return this.drizzleClient;
  }

  /**
   * Getter for the "concrete" ENSIndexer Schema definition used in the Drizzle client
   * for ENSDb instance.
   *
   * Useful while working on complex queries for ENSDb.
   *
   * Note: using `ensIndexerSchema` name for this getter to make it read better
   * in the context of query building. For example:
   * `this.ensDb.select().from(this.ensIndexerSchema.event)` vs.
   * `this.ensDb.select().from(this.concreteEnsIndexerSchema.event)`.
   */
  get ensIndexerSchema(): ConcreteEnsIndexerSchema {
    return this._concreteEnsIndexerSchema;
  }

  /**
   * Getter for the ENSIndexer Schema Name used by this ENSDbReader instance.
   */
  get ensIndexerSchemaName(): string {
    return this._ensIndexerSchemaName;
  }

  /**
   * Getter for the ENSNode Schema definition used in the Drizzle client
   * for ENSDb instance.
   *
   * Useful while working on complex queries for ENSDb.
   */
  get ensNodeSchema(): EnsNodeSchema {
    return this._ensNodeSchema;
  }

  /**
   * Check if the ENSDb instance is healthy by running a simple query
   * against it.
   */
  async isHealthy(): Promise<boolean> {
    try {
      await this.ensDb.execute("SELECT 1;");
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Check if the ENSDb instance is ready by verifying that
   * the {@link IndexingMetadataContext} has been initialized for
   * the ENSIndexer Schema used by this ENSDbReader instance.
   */
  async isReady(): Promise<boolean> {
    try {
      const indexingMetadataContext = await this.getIndexingMetadataContext();
      return indexingMetadataContext.statusCode === IndexingMetadataContextStatusCodes.Initialized;
    } catch {
      return false;
    }
  }

  /**
   * Build ENSDb Public Config
   */
  async buildEnsDbPublicConfig(): Promise<EnsDbPublicConfig> {
    const versionInfo = await this.buildEnsDbVersionInfo();

    return {
      versionInfo,
    };
  }

  /**
   * Get Indexing Metadata Context
   *
   * @returns the initialized record, or a default uninitialized one if no record exists in ENSDb.
   */
  async getIndexingMetadataContext(): Promise<IndexingMetadataContext> {
    const record = await this.getEnsNodeMetadata<SerializedEnsNodeMetadataIndexingMetadataContext>({
      key: EnsNodeMetadataKeys.IndexingMetadataContext,
    });

    if (!record) {
      return buildIndexingMetadataContextUninitialized();
    }

    return deserializeIndexingMetadataContext(record);
  }

  /**
   * Destroy the ENSDbReader instance by closing the ENSDb connection pool.
   */
  async destroy(): Promise<void> {
    // @ts-expect-error - Drizzle Client does not have `end` method in its type definition,
    // but it does exist in the actual implementation and is necessary to properly close
    // the connection pool to the ENSDb instance when destroying the ENSDbReader instance.
    await this.ensDb.$client.end();
  }

  /**
   * Get ENSNode Metadata record
   *
   * @returns selected record in ENSDb.
   * @throws when more than one matching metadata record is found
   *         (should be impossible given the composite PK constraint on
   *         'ensIndexerSchemaName' and 'key')
   */
  private async getEnsNodeMetadata<EnsNodeMetadataType extends SerializedEnsNodeMetadata>(
    metadata: Pick<EnsNodeMetadataType, "key">,
  ): Promise<EnsNodeMetadataType["value"] | undefined> {
    const result = await this.ensDb
      .select()
      .from(this.ensNodeSchema.metadata)
      .where(
        and(
          eq(this.ensNodeSchema.metadata.ensIndexerSchemaName, this.ensIndexerSchemaName),
          eq(this.ensNodeSchema.metadata.key, metadata.key),
        ),
      );

    if (result.length === 0) {
      return undefined;
    }

    if (result.length === 1 && result[0]) {
      return result[0].value as EnsNodeMetadataType["value"];
    }

    throw new Error(
      `There must be exactly one ENSNodeMetadata record for ('${this.ensIndexerSchemaName}', '${metadata.key}') composite key`,
    );
  }

  /**
   * Get PostgreSQL version for the server hosting the ENSDb instance.
   *
   * @throws when the version cannot be retrieved or parsed from the query result.
   */
  private async getPostgresVersion(): Promise<string> {
    const result = await this.ensDb.execute<{ version: string }>("SELECT version();");

    // result will be in the form of [{ version: "PostgreSQL 15.5 (Ubuntu 15.5-0ubuntu0.22.04.1) ..." }]
    const versionString = result.rows[0]?.version;

    try {
      return parsePgVersionInfo(versionString);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      throw new Error(`Failed to get PostgreSQL version for the ENSDb instance: ${errorMessage}`);
    }
  }

  /**
   * Build ENSDb version info.
   *
   * @throws when version info cannot be retrieved or parsed from
   *         the ENSDb instance.
   */
  private async buildEnsDbVersionInfo(): Promise<EnsDbVersionInfo> {
    const postgresVersion = await this.getPostgresVersion();

    return {
      postgresql: postgresVersion,
    };
  }
}
